/* savecase-auto-continue
 *
 * There is one local store key:
 * sessionId-{modelid}: The last known session ID for this local browser.
 * {modelid} appended to keys to avoid overlap between models served on the same
 * domain.
 *
 * When the user first runs a model, the session ID is stored in local storage.  When a
 * user re-enters the page, the option to continue from the stored session is offered.
 *
 * Dependencies:
 *  Dialogue.js
 *  Server must support saving cases.
 *  Set savemode to 'everystep' for the model in question!
 *  Set ignorerunningsessions to yes
 *  Model should be anon.
 *
 * Author: Tim Bauwens
 * Copyright 2024 xxllnc
 *
 */

import { _, bb } from "$json";
import { render, html } from "lit-html";
import Dialogue from "$Dialogue";
import { Mode } from "$json/lib/mode.js";
import { registerErrorHandler, CODES } from "$json/lib/errors.js";
const url = new URL(location);

const ftButtonStartNewSession = () =>
  html`<button id="start-new-session">
    <?xml version="1.0" encoding="utf-8"?>
    <svg
      version="1.2"
      baseProfile="tiny"
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width="20px"
      height="20px"
      viewBox="0 0 24 24"
    >
      <g id="_x32_4px_Boxes">
        <rect fill="none" width="24" height="24" />
      </g>
      <g id="Production">
        <g>
          <path
            fill="currentColor"
            d="M5,21c0,0.5527,0.4473,1,1,1h12c0.5527,0,1-0.4473,1-1V8H5V21z M15,10h2v9l-2,1V10z M11,10h2v9l-2,1V10z M7,10h2v9l-2,1
                        V10z"
          />
          <path
            fill="currentColor"
            d="M19,4h-3V3c0-0.5527-0.4473-1-1-1H9C8.4473,2,8,2.4473,8,3v1H5C4.4473,4,4,4.4473,4,5v2h1h14h1V5
                        C20,4.4473,19.5527,4,19,4z"
          />
        </g>
      </g>
    </svg>

    ${_("Start fresh")}
  </button>`;

((doc, $) => {
  const DICTIONARY = {
    "A dialogue popup to allow you to continue where you left from last": {
      "en": "A dialogue popup to allow you to continue where you left from last",
      "nl": "Een dialoogpop-up waarmee u verder kunt gaan waar u de vorige keer was gebleven",
      "en-GB":
        "A dialogue popup to allow you to continue where you left from last"
    },
    "Continue or start fresh?": {
      "en": "Continue or start fresh?",
      "nl": "Verder gaan of opnieuw beginnen?",
      "en-GB": "Continue or start fresh?"
    },
    "The continued session is invalid, do you want to start fresh?": {
      "en": "The continued session is invalid, do you want to start fresh?",
      "nl": "De voortgezette sessie is niet meer beschikbaar, wil je opnieuw beginnen?",
      "en-GB": "The continued session is invalid, do you want to start fresh?"
    },
    "Welcome back! Your previous session is still available. Would you like to continue or start over?":
      {
        "en": "Welcome back! Your previous session is still available. Would you like to continue or start over?",
        "nl": "Welkom terug! Je antwoorden van een eerdere sessie zijn lokaal opgeslagen. Wil je daarmee verder gaan of opnieuw beginnen?",
        "en-GB":
          "Welcome back! Your previous session is still available. Would you like to continue or start over?"
      },
    "Welcome back! Sadly enough, your previous session is not available anymore. Would you like to start over?":
      {
        en: "Welcome back! Sadly enough, your previous session is not available anymore. Would you like to start over?",
        nl: "Welkom terug! Helaas is je vorige sessie niet meer beschikbaar, wil je opnieuw beginnen?"
      },
    "Continue": {
      "en": "Continue",
      "nl": "Verder gaan",
      "en-GB": "Continue"
    },
    "Start fresh": {
      "en": "Start fresh",
      "nl": "Wis en start opnieuw",
      "en-GB": "Start fresh"
    }
  };
  const dialogue = new Dialogue({
    "class": "p-savecase-auto-continue-dialogue",
    "aria-label": _(
      "A dialogue popup to allow you to continue where you left from last"
    )
  });
  const location = bb.propFinder(bb.conf)(
    "arbitrary.savecase-email-link-popup.location",
    `${window.location.protocol}//${window.location.host}${window.location.pathname}`
  );

  _.addTranslations(DICTIONARY);

  const offerContinuation = ({ storedSessionId, sessionKey, data }) => {
    // Open a dialogue to offer the choice
    const choiceElem = document.createElement("div");
    render(
      html`
        <h1> ${_("Continue or start fresh?")} </h1>
        <p>
          ${_(
            "Welcome back! Your previous session is still available. Would you like to continue or start over?"
          )}
        </p>
        <ul>
          <li>
            <button id="continue-session">
              <?xml version="1.0" encoding="utf-8"?>
              <svg
                version="1.2"
                baseProfile="tiny"
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                width="35px"
                height="35px"
                viewBox="0 0 64 64"
              >
                <g id="_x36_4px_Boxes">
                  <rect fill="none" width="64" height="64" />
                </g>
                <g id="Production">
                  <path
                    fill="currentColor"
                    d="M51.0996,29.3403l-19-15c-1.2715-1.0058-3.0801-0.9682-4.3096,0.0909c-1.2295,1.0576-1.5371,2.8388-0.7334,4.248
                      L31.8025,27H14.5264C11.8096,27,10.999,29.3491,11,32.0015C10.999,34.6519,11.8086,37,14.5264,37h23.7802
                      c0,0,0.1543,0.2578-0.3216,0.3863c-0.5962,0.161-7.5495,2.0104-7.5495,2.0104l-3.3789,5.9241
                      c-0.8037,1.4092-0.4961,3.1904,0.7334,4.248c0.6338,0.5459,1.4219,0.8199,2.21,0.8199c0.7412,0,1.4834-0.2417,2.0996-0.729l19-15
                      c0.8145-0.6426,1.2891-1.6226,1.2891-2.6597C52.3887,30.9629,51.9141,29.9829,51.0996,29.3403z"
                  />
                </g>
              </svg>
              ${_("Continue")}
            </button>
          </li>
          <li>${ftButtonStartNewSession()}</li>
        </ul>
      `,
      choiceElem
    );

    // Render the dialogue
    dialogue.render(choiceElem);

    // Attach event listeners after rendering
    choiceElem
      .querySelector("#continue-session")
      .addEventListener("click", () => {
        // User chooses to continue the session
        dialogue.destroy();
        let destination = `${location}?modelid=${data.modelid}&sessionid=${storedSessionId}`;
        window.location.assign(destination);
      });

    choiceElem
      .querySelector("#start-new-session")
      .addEventListener("click", () => {
        // User chooses to start a new session
        localStorage.setItem(sessionKey, data.sessionid);
        dialogue.destroy();
      });
  };

  const offerFreshOne = () => {
    // Open a dialogue to offer the choice
    const choiceElem = document.createElement("div");
    render(
      html`
        <h1>
          ${_("The continued session is invalid, do you want to start fresh?")}
        </h1>
        <p>
          ${_(
            "Welcome back! Sadly enough, your previous session is not available anymore. Would you like to start over?"
          )}
        </p>
        <div class="single-button"> ${ftButtonStartNewSession()} </div>
      `,
      choiceElem
    );

    // Render the dialogue
    dialogue.render(choiceElem);

    choiceElem
      .querySelector("#start-new-session")
      .addEventListener("click", () => {
        // User chooses to start a new session
        const sp = url.searchParams;
        const sessionKey = `sessionId-${sp.get("modelid")}`;
        localStorage.removeItem(sessionKey);
        dialogue.destroy();
        let destination = `${location}?modelid=${sp.get("modelid")}`;
        window.location.assign(destination);
      });
  };

  registerErrorHandler(CODES.cWebOpenIsCalledWithUniqueID, (code, data) => {
    offerFreshOne();
    console.log(code, data);
  });

  $(doc).on("bb:finalHandleData", (e, data) => {
    /**
     * If local store has no session ID, then record time and sesssion
     * If local store has session ID, and it doesn't match current one:
     *    Offer to continue new, continue from store session
     *      Continue new: Replace session ID.
     *      Continue from store: Replace session ID, redirect to (layer dictated) continue path
     */

    if (data && data.sessionid) {
      const sessionKey = `sessionId-${data.modelid}`;
      const storedSessionId = localStorage.getItem(sessionKey);
      if (!storedSessionId || Mode.get("isContinued")) {
        // No session ID in storage, store it now.
        localStorage.setItem(sessionKey, data.sessionid);
      } else if (storedSessionId !== data.sessionid) {
        offerContinuation({ storedSessionId, sessionKey, data });
      }
    }
  });
})(document, jQuery);
